import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, 
  Filter,
  Clapperboard,
  Download,
  Trash2,
  Heart,
  MoreVertical,
  Grid,
  List,
  Calendar,
  FileImage,
  Loader2,
  Copy,
  RefreshCw,
  Star,
  Maximize2,
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Scissors,
  PauseCircle,
  Play,
  Printer
} from 'lucide-react';
import Button from '../../components/Button';
import { libraryService, Mockup } from '../../services/libraryService';
import { VideoMockup, videoMockupService } from '../../services/videoMockupService';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { useAuthRefresh } from '../../hooks/useAuthRefresh';
import { API_BASE_URL } from '../../utils/apiUrl';

const statusLabel = (status: string) => {
  switch (status) {
    case 'created':
      return 'Queued';
    case 'queueing':
      return 'Queueing';
    case 'processing':
      return 'Rendering';
    case 'success':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
};

const Library: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mockups, setMockups] = useState<Mockup[]>([]);
  const [videoMockups, setVideoMockups] = useState<VideoMockup[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const [libraryTab, setLibraryTab] = useState<'images' | 'videos'>('images');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filter, setFilter] = useState<'all' | 'favorites'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMockup, setSelectedMockup] = useState<Mockup | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [previewMockup, setPreviewMockup] = useState<Mockup | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [removingBackground, setRemovingBackground] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<VideoMockup | null>(null);
  const [videoDeleting, setVideoDeleting] = useState<string | null>(null);
  const [videoDownloading, setVideoDownloading] = useState<string | null>(null);
  const [isVideoPreviewPlaying, setIsVideoPreviewPlaying] = useState(true);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const itemsPerPage = 12;
  const previewId = searchParams.get('preview');

  // Helper function to get full image URL
  const getImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const apiUrl = API_BASE_URL;
    return `${apiUrl}${url}`;
  };

  // Use auth refresh hook to reload data when session refreshes
  useAuthRefresh(() => {
    if (user && !authLoading) {
      console.log('Auth refreshed, reloading library data...');
      loadMockups();
      void loadVideoMockups();
    }
  });

  // Initial load
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setMockups([]);
      setVideoMockups([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    loadMockups();
    void loadVideoMockups();
  }, [user, authLoading]);

  // Load when filters change
  useEffect(() => {
    // Skip the initial mount
    if (libraryTab === 'images' && (mockups.length > 0 || totalCount > 0 || loadError)) {
      loadMockups();
    }
  }, [filter, searchQuery, page, libraryTab]);

  useEffect(() => {
    if (!previewId || mockups.length === 0) {
      return;
    }

    const previewMatch = mockups.find((mockup) => mockup.id === previewId);
    if (!previewMatch) {
      return;
    }

    setPreviewMockup(previewMatch);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('preview');
    setSearchParams(nextParams, { replace: true });
  }, [mockups, previewId, searchParams, setSearchParams]);

  // Keyboard shortcuts for preview modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!previewMockup) return;

      switch (e.key) {
        case 'Escape':
          setPreviewMockup(null);
          setZoomLevel(1);
          setRotation(0);
          break;
        case '+':
        case '=':
          setZoomLevel(prev => Math.min(3, prev + 0.25));
          break;
        case '-':
        case '_':
          setZoomLevel(prev => Math.max(0.5, prev - 0.25));
          break;
        case 'r':
        case 'R':
          setRotation(prev => (prev + 90) % 360);
          break;
        case 'd':
        case 'D':
          handleDownload(previewMockup);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [previewMockup]);

  useEffect(() => {
    if (!videoPreview || !videoPreviewRef.current) {
      return;
    }

    videoPreviewRef.current.currentTime = 0;
    const playPromise = videoPreviewRef.current.play();
    if (playPromise) {
      void playPromise
        .then(() => setIsVideoPreviewPlaying(true))
        .catch(() => setIsVideoPreviewPlaying(false));
    }
  }, [videoPreview]);

  const loadMockups = async () => {
    let loadTimeout: NodeJS.Timeout;
    
    try {
      setLoading(true);
      setLoadError(null);
      
      // Set a timeout to prevent infinite loading
      loadTimeout = setTimeout(() => {
        setLoading(false);
        setLoadError('Loading took too long. Please refresh the page.');
        console.warn('Library loading timeout');
      }, 10000); // 10 second timeout
      
      const { mockups: data, count, error } = await libraryService.getUserMockups({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        favoritesOnly: filter === 'favorites',
        searchQuery: searchQuery || undefined
      });

      clearTimeout(loadTimeout);

      if (error) {
        console.error('Error loading mockups:', error);
        const errorMessage = getErrorMessage(error);
        setLoadError(errorMessage);
        showNotification('error', 'Failed to load mockups', errorMessage);
      } else {
        setMockups(data);
        setTotalCount(count);
        setLoadError(null);
        console.log('Loaded mockups:', data.length);
      }
    } catch (error) {
      console.error('Failed to load mockups:', error);
      const errorMessage = getErrorMessage(error);
      setLoadError(errorMessage);
      showNotification('error', 'Failed to load mockups', errorMessage);
    } finally {
      clearTimeout(loadTimeout!);
      setLoading(false);
    }
  };

  const loadVideoMockups = async () => {
    try {
      const data = await videoMockupService.list();
      setVideoMockups(data);
    } catch (error) {
      console.error('Failed to load video mockups:', error);
    }
  };

  const handleDownload = async (mockup: Mockup) => {
    const result = await libraryService.downloadMockup(mockup);
    if (!result.success) {
      showNotification('error', 'Download failed', result.error || 'Failed to download mockup');
    } else {
      showNotification('success', 'Download started', `${mockup.name} is being downloaded`);
    }
  };

  const filteredVideoMockups = videoMockups.filter((videoMockup) => {
    if (!searchQuery.trim()) {
      return true;
    }

    const query = searchQuery.trim().toLowerCase();
    return (
      videoMockup.name.toLowerCase().includes(query)
      || (videoMockup.prompt || '').toLowerCase().includes(query)
      || (videoMockup.settings?.sourceMockupName || '').toLowerCase().includes(query)
    );
  });

  const handleVideoDownload = async (videoMockup: VideoMockup) => {
    try {
      setVideoDownloading(videoMockup.id);
      await videoMockupService.download(videoMockup);
      showNotification('success', 'Download started', `${videoMockup.name} is being downloaded`);
    } catch (error: any) {
      showNotification('error', 'Download failed', error.message || 'Failed to download video');
    } finally {
      setVideoDownloading(null);
    }
  };

  const handleVideoDelete = async (videoMockup: VideoMockup) => {
    if (!window.confirm(`Are you sure you want to delete "${videoMockup.name}"?`)) {
      return;
    }

    try {
      setVideoDeleting(videoMockup.id);
      await videoMockupService.remove(videoMockup.id);
      setVideoMockups((current) => current.filter((item) => item.id !== videoMockup.id));
      showNotification('success', 'Video deleted', `${videoMockup.name} has been permanently deleted`);
    } catch (error: any) {
      showNotification('error', 'Delete failed', error.message || 'Failed to delete video');
    } finally {
      setVideoDeleting(null);
    }
  };

  const toggleVideoPreviewPlayback = async () => {
    if (!videoPreviewRef.current) {
      return;
    }

    if (videoPreviewRef.current.paused) {
      await videoPreviewRef.current.play();
      setIsVideoPreviewPlaying(true);
      return;
    }

    videoPreviewRef.current.pause();
    setIsVideoPreviewPlaying(false);
  };

  const handleToggleFavorite = async (mockup: Mockup) => {
    const result = await libraryService.toggleFavorite(mockup.id);
    if (result.success) {
      loadMockups();
      showNotification('success', 
        mockup.is_favorite ? 'Removed from favorites' : 'Added to favorites',
        `${mockup.name} has been updated`
      );
    } else {
      showNotification('error', 'Failed to update favorite', result.error || 'An error occurred');
    }
  };

  const handleDelete = async (mockup: Mockup) => {
    if (!window.confirm(`Are you sure you want to delete "${mockup.name}"?`)) {
      return;
    }

    setIsDeleting(mockup.id);
    const result = await libraryService.deleteMockup(mockup.id);
    
    if (result.success) {
      loadMockups();
      showNotification('success', 'Mockup deleted', `${mockup.name} has been permanently deleted`);
    } else {
      showNotification('error', 'Delete failed', result.error || 'Failed to delete mockup');
    }
    setIsDeleting(null);
  };

  const handleRegenerateVariation = (mockup: Mockup) => {
    // Store mockup settings and navigate to customize page
    sessionStorage.setItem('regenerateFrom', JSON.stringify({
      template: mockup.template_name,
      settings: mockup.settings
    }));
    // Navigate to templates to start new flow with same settings
    window.location.href = '/dashboard/mockups';
  };

  const handleRemoveBackground = async (mockup: Mockup) => {
    if (!mockup.id || removingBackground) return;
    
    setRemovingBackground(mockup.id);
    
    try {
      const result = await libraryService.removeBackground(mockup.id, getImageUrl(mockup.mockup_url));
      
      if (result.error) {
        showNotification('error', 'Background removal failed', result.error);
      } else if (result.mockup) {
        // Show the result in the existing preview modal
        const resultUrl = result.mockup.url.startsWith('http') ? result.mockup.url : `${API_BASE_URL}${result.mockup.url}`;
        setPreviewMockup({
          ...mockup,
          mockup_url: resultUrl,
          name: `${mockup.name} (No Background)`,
        });
        showNotification('success', 'Background removed!', 'Showing preview.');
      }
    } catch (error: any) {
      console.error('Background removal error:', error);
      showNotification('error', 'Background removal failed', 'Failed to remove background');
    } finally {
      setRemovingBackground(null);
    }
  };

  const handleOpenPrintPrep = (mockup: Mockup) => {
    navigate(`/dashboard/mockup-to-print?mockup=${mockup.id}`);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && mockups.length === 0 && !loadError) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-brand-purple" />
          <p className="mt-4 text-gray-600">Loading your mockups...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="mb-4 p-3 bg-red-50 rounded-full inline-block">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Unable to Load Mockups</h3>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-h2 font-heading text-text-dark mb-2">
              My Library
            </h1>
            <p className="text-body text-body-grey">
              All your generated image and video mockups in one place
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-body-grey">
            <span>{libraryTab === 'images' ? `${totalCount} image mockups` : `${videoMockups.length} video mockups`}</span>
            <span>•</span>
            <span>{mockups.filter(m => m.is_favorite).length} favorites</span>
          </div>
        </div>

        <div className="mb-6 inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
          <button
            onClick={() => setLibraryTab('images')}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              libraryTab === 'images' ? 'bg-brand-purple text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Image Mockups
          </button>
          <button
            onClick={() => setLibraryTab('videos')}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              libraryTab === 'videos' ? 'bg-brand-purple text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Video Mockups
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search mockups..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            {libraryTab === 'images' && (
              <select
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value as 'all' | 'favorites');
                  setPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
              >
                <option value="all">All Mockups</option>
                <option value="favorites">Favorites Only</option>
              </select>
            )}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors border-l border-gray-300`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mockups Grid/List */}
      {libraryTab === 'images' ? (viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {mockups.map((mockup) => (
            <div key={mockup.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group">
              {/* Thumbnail */}
              <div className="aspect-[4/3] relative overflow-hidden rounded-t-xl bg-gray-100">
                <img 
                  src={getImageUrl(mockup.thumbnail_url || mockup.mockup_url)} 
                  alt={mockup.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewMockup(mockup)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Preview"
                    >
                      <Maximize2 className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleDownload(mockup)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Download"
                    >
                      <Download className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleToggleFavorite(mockup)}
                      className={`p-2 bg-white rounded-full hover:bg-gray-100 transition-colors ${
                        mockup.is_favorite ? 'text-red-500' : 'text-gray-700'
                      }`}
                      title="Toggle Favorite"
                    >
                      <Heart className={`w-5 h-5 ${mockup.is_favorite ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleRemoveBackground(mockup)}
                      disabled={removingBackground === mockup.id}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove Background"
                    >
                      {removingBackground === mockup.id ? (
                        <Loader2 className="w-5 h-5 text-gray-700 animate-spin" />
                      ) : (
                        <Scissors className="w-5 h-5 text-gray-700" />
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedMockup(mockup)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="More Options"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>
                </div>
                {mockup.is_favorite && (
                  <div className="absolute top-2 right-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-text-dark truncate mb-1">{mockup.name}</h3>
                <p className="text-sm text-body-grey mb-3 capitalize">{mockup.template_name ? mockup.template_name.replace(/-/g, ' ') : 'Custom Scene'}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(mockup.created_at), 'MMM d, yyyy')}
                  </span>
                  <span className="flex items-center">
                    <Download className="w-3 h-3 mr-1" />
                    {mockup.downloads}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mockup
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Downloads
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mockups.map((mockup) => (
                <tr key={mockup.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img 
                        src={getImageUrl(mockup.thumbnail_url || mockup.mockup_url)} 
                        alt={mockup.name}
                        className="w-10 h-10 rounded object-cover"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {mockup.name}
                          {mockup.is_favorite && (
                            <Star className="w-4 h-4 text-yellow-500 fill-current ml-2" />
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 capitalize">
                      {mockup.template_name ? mockup.template_name.replace(/-/g, ' ') : 'Custom Scene'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {format(new Date(mockup.created_at), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatFileSize(mockup.file_size)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{mockup.downloads}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setPreviewMockup(mockup)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Preview"
                      >
                        <Maximize2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(mockup)}
                        className="text-brand-purple hover:text-brand-purple/80"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleFavorite(mockup)}
                        className={`${mockup.is_favorite ? 'text-red-500' : 'text-gray-400'} hover:text-red-600`}
                        title="Toggle Favorite"
                      >
                        <Heart className={`w-4 h-4 ${mockup.is_favorite ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleDelete(mockup)}
                        className="text-red-600 hover:text-red-800"
                        disabled={isDeleting === mockup.id}
                        title="Delete"
                      >
                        {isDeleting === mockup.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )) : (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVideoMockups.map((videoMockup) => {
              const isReady = videoMockup.status === 'success' && videoMockup.video_url;

              return (
                <div key={videoMockup.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group">
                  <div className="aspect-[4/3] relative overflow-hidden rounded-t-xl bg-gray-100">
                    {isReady ? (
                      <video
                        src={getImageUrl(videoMockup.video_url || '')}
                        poster={getImageUrl(videoMockup.thumbnail_url || videoMockup.settings?.sourceThumbnailUrl || '')}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        onMouseEnter={(event) => void event.currentTarget.play().catch(() => undefined)}
                        onMouseLeave={(event) => {
                          event.currentTarget.pause();
                          event.currentTarget.currentTime = 0;
                        }}
                      />
                    ) : (
                      <img
                        src={getImageUrl(videoMockup.thumbnail_url || videoMockup.settings?.sourceThumbnailUrl || '')}
                        alt={videoMockup.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute top-2 right-2 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-gray-900">
                      {videoMockup.status}
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex gap-2">
                        <button
                          onClick={() => isReady && setVideoPreview(videoMockup)}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                          disabled={!isReady}
                          title="Preview"
                        >
                          <Play className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                          onClick={() => isReady && handleVideoDownload(videoMockup)}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                          disabled={!isReady || videoDownloading === videoMockup.id}
                          title="Download"
                        >
                          {videoDownloading === videoMockup.id ? (
                            <Loader2 className="w-5 h-5 text-gray-700 animate-spin" />
                          ) : (
                            <Download className="w-5 h-5 text-gray-700" />
                          )}
                        </button>
                        <button
                          onClick={() => handleVideoDelete(videoMockup)}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                          disabled={videoDeleting === videoMockup.id}
                          title="Delete"
                        >
                          {videoDeleting === videoMockup.id ? (
                            <Loader2 className="w-5 h-5 text-gray-700 animate-spin" />
                          ) : (
                            <Trash2 className="w-5 h-5 text-gray-700" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-text-dark truncate mb-1">{videoMockup.name}</h3>
                    <p className="text-sm text-body-grey mb-3 capitalize">
                      {videoMockup.duration}s video • {videoMockup.resolution}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {format(new Date(videoMockup.created_at), 'MMM d, yyyy')}
                      </span>
                      <span className="inline-flex items-center">
                        <Clapperboard className="w-3 h-3 mr-1" />
                        {statusLabel(videoMockup.status)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredVideoMockups.map((videoMockup) => {
                  const isReady = videoMockup.status === 'success' && videoMockup.video_url;

                  return (
                    <tr key={videoMockup.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img
                            src={getImageUrl(videoMockup.thumbnail_url || videoMockup.settings?.sourceThumbnailUrl || '')}
                            alt={videoMockup.name}
                            className="w-10 h-10 rounded object-cover"
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{videoMockup.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{statusLabel(videoMockup.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format(new Date(videoMockup.created_at), 'MMM d, yyyy')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{videoMockup.duration}s • {videoMockup.resolution}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => isReady && setVideoPreview(videoMockup)}
                            className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                            disabled={!isReady}
                            title="Preview"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => isReady && handleVideoDownload(videoMockup)}
                            className="text-brand-purple hover:text-brand-purple/80 disabled:opacity-50"
                            disabled={!isReady || videoDownloading === videoMockup.id}
                            title="Download"
                          >
                            {videoDownloading === videoMockup.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleVideoDelete(videoMockup)}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            disabled={videoDeleting === videoMockup.id}
                            title="Delete"
                          >
                            {videoDeleting === videoMockup.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {libraryTab === 'videos' && filteredVideoMockups.length === 0 && (
        <div className="text-center py-12">
          <Clapperboard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No video mockups found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery ? 'Try adjusting your search terms' : 'Create a video mockup to see it here'}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              size="medium"
              onClick={() => window.location.href = '/dashboard/video-mockups'}
            >
              Create Your First Video
            </Button>
          )}
        </div>
      )}

      {/* Empty State */}
      {libraryTab === 'images' && mockups.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileImage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No mockups found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery ? 'Try adjusting your search terms' : 'Create your first mockup to see it here'}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              size="medium"
              onClick={() => window.location.href = '/dashboard/mockups'}
            >
              Create Your First Mockup
            </Button>
          )}
        </div>
      )}

      {/* Pagination */}
      {libraryTab === 'images' && totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* More Options Modal */}
      {selectedMockup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedMockup(null)}
        >
          <div 
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mockup Options</h3>
            <div className="space-y-2">
              <button
                onClick={() => {
                  handleDownload(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Download Mockup</span>
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(getImageUrl(selectedMockup.mockup_url));
                  alert('Link copied to clipboard!');
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Copy className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Copy Link</span>
              </button>
              <button
                onClick={() => {
                  handleOpenPrintPrep(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Printer className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Prepare for Print</span>
              </button>
              <button
                onClick={() => {
                  handleRegenerateVariation(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Generate Variation</span>
              </button>
              <button
                onClick={() => {
                  handleDelete(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 rounded-lg transition-colors text-red-600"
              >
                <Trash2 className="w-5 h-5" />
                <span>Delete Mockup</span>
              </button>
            </div>
            <button
              onClick={() => setSelectedMockup(null)}
              className="mt-4 w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewMockup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setPreviewMockup(null);
            setZoomLevel(1);
            setRotation(0);
          }}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewMockup(null);
                setZoomLevel(1);
                setRotation(0);
              }}
              className="absolute top-4 right-4 p-2 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Image container */}
            <div 
              className="relative overflow-auto max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={getImageUrl(previewMockup.mockup_url)} 
                alt={previewMockup.name}
                className="max-w-full max-h-full object-contain transition-transform duration-300"
                style={{
                  transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                  cursor: zoomLevel > 1 ? 'move' : 'default'
                }}
              />
            </div>

            {/* Control bar */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel(prev => Math.max(0.5, prev - 0.25));
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="w-5 h-5 text-white" />
              </button>
              
              <span className="text-white text-sm min-w-[60px] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel(prev => Math.min(3, prev + 0.25));
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="w-5 h-5 text-white" />
              </button>

              <div className="w-px h-6 bg-white/30" />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRotation(prev => (prev + 90) % 360);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Rotate"
              >
                <RotateCw className="w-5 h-5 text-white" />
              </button>

              <div className="w-px h-6 bg-white/30" />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(previewMockup);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5 text-white" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPrintPrep(previewMockup);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Prepare for Print"
              >
                <Printer className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Mockup info */}
            <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-md rounded-lg px-4 py-2">
              <h3 className="text-white font-semibold">{previewMockup.name}</h3>
              <p className="text-white/80 text-sm">
                {previewMockup.width && previewMockup.height && 
                  `${previewMockup.width} × ${previewMockup.height} • `
                }
                {formatFileSize(previewMockup.file_size)}
              </p>
            </div>

            {/* Keyboard hints */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-md rounded-lg px-4 py-2 text-white/80 text-sm">
              <kbd className="px-2 py-1 bg-white/20 rounded">ESC</kbd> Close • 
              <kbd className="px-2 py-1 bg-white/20 rounded ml-2">+/-</kbd> Zoom • 
              <kbd className="px-2 py-1 bg-white/20 rounded ml-2">R</kbd> Rotate • 
              <kbd className="px-2 py-1 bg-white/20 rounded ml-2">D</kbd> Download
            </div>
          </div>
        </div>
      )}

      {videoPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setVideoPreview(null)}
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh] w-full max-w-5xl bg-black rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoPreview(null)}
              className="absolute top-4 right-4 p-2 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <div className="p-4 sm:p-6">
              <video
                ref={videoPreviewRef}
                src={getImageUrl(videoPreview.video_url || '')}
                poster={getImageUrl(videoPreview.thumbnail_url || videoPreview.settings?.sourceThumbnailUrl || '')}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onPlay={() => setIsVideoPreviewPlaying(true)}
                onPause={() => setIsVideoPreviewPlaying(false)}
                className="w-full max-h-[75vh] rounded-xl"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-black/80 px-6 py-4">
              <div>
                <h3 className="text-white font-semibold">{videoPreview.name}</h3>
                <p className="text-white/70 text-sm">
                  {videoPreview.duration}s • {videoPreview.resolution} • {statusLabel(videoPreview.status)}
                </p>
              </div>
              <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  void toggleVideoPreviewPlayback();
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {isVideoPreviewPlaying ? <PauseCircle className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isVideoPreviewPlaying ? 'Pause Video' : 'Play Video'}
              </button>
              <button
                onClick={() => void handleVideoDownload(videoPreview)}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100"
              >
                <Download className="w-4 h-4" />
                Download MP4
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Library;
